import React, { useContext, useState } from 'react';
import { Box, Typography } from '@material-ui/core';

import { useMutation, useLazyQuery } from '@apollo/client';
import { usePostHog } from 'posthog-js/react';

import { useParams } from 'react-router-dom';

import { isValidPhoneNumber } from 'react-phone-number-input';

import { useTranslation } from 'react-i18next';

import Styled from './PatientInfo.styles';

import { Titles } from '../../components/Titles/Titles';
import { DataSharingInfo } from '../../components/DataSharingInfo/DataSharingInfo';
import { OnboardingFlowContext } from '../../OnboardingFlowContext';
import { WhyDoWeNeedThisInfo } from '../../components/OnboardingPopover/popoverContent/WhyDoWeNeedThisInfo';
import {
  TermsAndConditionsAcknowledgements,
} from '../../components/TermsAndConditionsAcknowledgements/TermsAndConditionsAcknowledgements';
import validatePatientInfoForm from './validatePatientInfoForm';
import { StatePicker } from '../../components/StatePicker/StatePicker';

import { OnboardingParams } from '../../types';

import { ErrorText } from '../../components/ErrorText/ErrorText';

import { PhoneNumberInput } from '../../components/PhoneNumberInput/PhoneNumberInput';

import { isEmailError } from '../../utils/errorHelpers';

import { Button, TextField } from '@/components';
import { validateEmail } from '@/utils/stringValidators';
import { getPdtUid, isSpark as isSparkHelper } from '@/utils/productHelpers';
import { convertDOBToDateString } from '@/utils/stringUtils';
import { ENROLL_PATIENT } from '@/apollo/mutations';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { EN } from '@/utils/constants';
import { EnrollDirectAccessPatientMutationVariables } from '@/apollo/__generated__/graphql';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { logError } from '@/utils/logError';

// fields that we *can* scrub before sending data to the BE
const nullableFields = ['address', 'city', 'state', 'postalCode'];

export const PatientInfo: React.FC = () => {
  const {
    next,
    updateFormData,
    onboardingFormData,
    eligibilityFormCustomization,
    uid,
  } = useContext(OnboardingFlowContext);
  useAcceptedLanguage();

  const { t, i18n } = useTranslation();
  const { product, slug } = useParams<OnboardingParams>();
  const isSpark = isSparkHelper(product);
  const effectiveEligibility = {
    ...eligibilityFormCustomization,
    showAddressField: eligibilityFormCustomization.showAddressField && !isSpark,
  };
  const [popoverOpen, setPopoverOpen] = useState(false);
  const formValid = validatePatientInfoForm(onboardingFormData, effectiveEligibility, product);
  // validate each text field individually. used for error states and helper text...
  const [patientEmailFieldError, setPatientEmailFieldError] = useState(false);
  const [patientStateFieldError, setPatientStateFieldError] = useState(false);
  const [patientPhoneNumberFieldError, setPatientPhoneNumberFieldError] = useState(false);
  const [patientAddressFieldError, setPatientAddressFieldError] = useState(false);
  const [patientCityFieldError, setPatientCityFieldError] = useState(false);
  const [generalErrorMessage, setGeneralErrorMessage] = useState(undefined);

  const [enrollPatient, { loading }] = useMutation(ENROLL_PATIENT);
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, {
    fetchPolicy: 'network-only',
  });
  const posthog = usePostHog();

  // only show address field if the organization has the address field enabled
  const { showAddressField } = eligibilityFormCustomization;

  const handlePatientInfo = async () => {
    setGeneralErrorMessage(undefined);
    // if this value is caregiver we need to move on to the next part of the flow before we enroll patient
    if (onboardingFormData.personEnrolling === 'caregiver') {
      next();
      return;
    }

    try {
      const currentISODate = new Date().toISOString();
      const patientSubmissionData = {
        address: onboardingFormData.patientStreetAddress,
        city: onboardingFormData.patientCity,
        dateOfBirth: convertDOBToDateString(onboardingFormData.patientDateOfBirth),
        email: onboardingFormData.patientEmail,
        emergencyContactEmail: onboardingFormData.caregiverEmail,
        emergencyContactName: onboardingFormData.caregiverFirstName,
        emergencyContactPhone: onboardingFormData.caregiverPhoneNumber,
        firstName: onboardingFormData.patientFirstName,
        lastName: onboardingFormData.patientLastName,
        organizationSlug: slug,
        pdtUid: getPdtUid(product),
        phone: onboardingFormData.patientPhoneNumber,
        postalCode: onboardingFormData.patientZipCode,
        state: onboardingFormData.patientState,
        uid,
        safetyAcknowledgedAt: !onboardingFormData.safetyAcknowledgement ? null : currentISODate,
        termsAndPrivacyAcknowledgedAt: onboardingFormData.policyAndTermsAcknowledgement && currentISODate,
        language: i18n.language.toUpperCase().split('-')[0] || EN,
      };

      // remove any "nullable" fields from the submission data
      const cleanedPatientSubmissionData = Object.entries(patientSubmissionData)
        .reduce((acc, [k, v]) => ((!v && nullableFields.includes(k)) ? acc : { ...acc, [k]: v }), {});

      const result = await enrollPatient({
        // using object.entries and reduce we removed the type information from this object
        // so we need to cast it back to the correct type
        variables: { ...cleanedPatientSubmissionData as EnrollDirectAccessPatientMutationVariables },
      });

      if (result?.data?.enrollDirectAccessPatient?.success === true) {
        // creating the user in our system was successful

        try {
          const { data: idData } = await getDistinctId({
            variables: { email: onboardingFormData.patientEmail },
          });

          if (idData?.posthogDistinctIdWithEmail?.distinctId) {
            posthog?.identify(idData.posthogDistinctIdWithEmail.distinctId, {
              email: onboardingFormData.patientEmail,
            });
          }
        } catch (error) {
          logError('[PatientInfo]', 'PostHog distinct ID fetch failed', error);
        }

        next();
      } else if (isEmailError(result?.errors)) {
        setPatientEmailFieldError(true);
      } else {
        // creating the user in our system was UNsuccessful
        // Log diagnostic info (error messages, success status) without exposing
        // patient PII from the full mutation result.
        logError('[PatientInfo]', 'profile creation failed', undefined, {
          level: 'warning',
          extra: {
            errors: result?.errors?.map(({ message }) => message),
            success: result?.data?.enrollDirectAccessPatient?.success,
          },
        });
        setGeneralErrorMessage(t('coverage.errors.generic_error'));
      }
    } catch (e) {
      // There was a server error
      logError('[PatientInfo]', 'enrollment exception', e);
      setGeneralErrorMessage(t('coverage.errors.generic_error'));
    }
  };

  return (
    <>
      <Titles
        title={t(`coverage.${product}.${onboardingFormData.personEnrolling}.patient_title`)}
      />
      <Styled.HoverContainer
        onClick={() => setPopoverOpen(!popoverOpen)}
      >
        <Typography>
          <u>{t(`coverage.${product}.${onboardingFormData.personEnrolling}.patient_subtitle`)}</u>
        </Typography>
      </Styled.HoverContainer>
      <Typography variant="h2">
        {t(`coverage.${product}.${onboardingFormData.personEnrolling}.patient_email_title`)}
      </Typography>
      <Typography>
        {t(`coverage.${product}.${onboardingFormData.personEnrolling}.patient_email_subtitle`)}
      </Typography>

      <TextField
        required
        error={patientEmailFieldError}
        helperText={patientEmailFieldError ? t('coverage.errors.email_error') : ''}
        label={t(`coverage.${product}.${onboardingFormData.personEnrolling}.email_label`)}
        id="patientEmail"
        inputProps={{
          placeholder: t(`coverage.${product}.${onboardingFormData.personEnrolling}.email_label`),
        }}
        value={onboardingFormData.patientEmail}
        onChange={(e) => {
          setPatientEmailFieldError(false);
          updateFormData({ patientEmail: e.target.value });
        }}
        onBlur={(e) => {
          setPatientEmailFieldError(!validateEmail(e.target.value));
        }}
      />
      <Typography variant="h2">
        {t(`coverage.${product}.${onboardingFormData.personEnrolling}.patient_info_title`)}
      </Typography>
      <PhoneNumberInput
        placeholder={t(`coverage.${product}.${onboardingFormData.personEnrolling}.phone_label`)}
        value={onboardingFormData.patientPhoneNumber}
        id="patientPhoneNumber"
        onChange={(value) => {
          setPatientPhoneNumberFieldError(false);
          updateFormData({ patientPhoneNumber: value || '' });
        }}
        onBlur={() => {
          setPatientPhoneNumberFieldError(!isValidPhoneNumber(onboardingFormData.patientPhoneNumber));
        }}
        error={patientPhoneNumberFieldError}
        helperText={patientPhoneNumberFieldError ? t('coverage.errors.phone_error') : ''}
      />
      {showAddressField && !isSpark
        && (
          <>
            <TextField
              required
              error={patientAddressFieldError}
              helperText={patientAddressFieldError ? t('coverage.errors.street_error') : ''}
              label={t(`coverage.${product}.${onboardingFormData.personEnrolling}.street_label`)}
              id="patientStreetAddress"
              inputProps={{
                placeholder: t(`coverage.${product}.${onboardingFormData.personEnrolling}.street_label`),
              }}
              value={onboardingFormData.patientStreetAddress}
              onChange={(e) => {
                setPatientAddressFieldError(false);
                updateFormData({ patientStreetAddress: e.target.value });
              }}
              onBlur={(e) => {
                setPatientAddressFieldError(e.target.value.trim() === '');
              }}
            />
            <TextField
              required
              error={patientCityFieldError}
              helperText={patientCityFieldError ? t('coverage.errors.city_error') : ''}
              label={t(`coverage.${product}.${onboardingFormData.personEnrolling}.city_label`)}
              id="patientCity"
              inputProps={{
                placeholder: t(`coverage.${product}.${onboardingFormData.personEnrolling}.city_label`),
              }}
              value={onboardingFormData.patientCity}
              onChange={(e) => {
                setPatientCityFieldError(false);
                updateFormData({ patientCity: e.target.value });
              }}
              onBlur={(e) => {
                setPatientCityFieldError(e.target.value.trim() === '');
              }}
            />
            <StatePicker
              error={patientStateFieldError}
              helperText={patientStateFieldError ? t('coverage.pre_onboarding.state_error') : ''}
              value={onboardingFormData.patientState}
              label={t(`coverage.${product}.${onboardingFormData.personEnrolling}.state_label`)}
              onBlur={(e) => {
                setPatientStateFieldError(e.target.value === '');
              }}
              onChange={(e) => {
                setPatientStateFieldError(false);
                updateFormData({ patientState: e.target.value });
              }}
            />
          </>
        )}
      <div
        style={{ marginBottom: '32px' }}
      />
      {onboardingFormData.personEnrolling === 'patient' && (
        <TermsAndConditionsAcknowledgements
          safetyAcknowledgement={onboardingFormData.safetyAcknowledgement}
          safetyAcknowledgementLabel={t('coverage.safety_acknowledgement_label')}
          policyAndTermsAcknowledgement={onboardingFormData.policyAndTermsAcknowledgement}
          onSelect={(data) => updateFormData(data)}
        />
      )}

      {generalErrorMessage && (
        <ErrorText error={generalErrorMessage} />
      )}
      <Box sx={{ width: '100%', justifyItems: 'center' }}>
        <Button
          data-testid="submit-button"
          onClick={handlePatientInfo}
          disabled={formValid === false}
          loading={loading}
          style={{
            borderRadius: 100,
          }}
        >
          {t('coverage.next')}
        </Button>
      </Box>
      <Box style={{ display: 'flex', justifyContent: 'center' }}>
        <DataSharingInfo />
      </Box>
      <WhyDoWeNeedThisInfo
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
        personEnrolling={onboardingFormData.personEnrolling}
      />
    </>
  );
};
